import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { WhatsAppButton } from "@/components/WhatsAppButton";

export const PublicNav = () => {
  const [open, setOpen] = useState(false);
  const { user, isAdmin } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const goHashSection = (id) => {
    setOpen(false);
    if (loc.pathname !== "/") {
      nav(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // sync hash for back-button correctness
    if (window.location.hash !== `#${id}`) {
      window.history.replaceState(null, "", `/#${id}`);
    }
  };

  const link = (to, label, testid) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      const active = loc.pathname === "/" && loc.hash === `#${id}`;
      return (
        <a
          href={to}
          onClick={(e) => { e.preventDefault(); goHashSection(id); }}
          data-testid={testid}
          className={`text-sm tracking-wide transition-colors cursor-pointer ${active ? "text-[#ff7a00]" : "text-[#cfcfcf] hover:text-[#ff9a2f]"}`}
        >
          {label}
        </a>
      );
    }
    return (
      <Link
        to={to}
        onClick={() => setOpen(false)}
        data-testid={testid}
        className={`text-sm tracking-wide transition-colors ${loc.pathname === to ? "text-[#ff7a00]" : "text-[#cfcfcf] hover:text-[#ff9a2f]"}`}
      >
        {label}
      </Link>
    );
  };
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/70 border-b border-[#ff7a00]/15">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {link("/", "Home", "nav-home")}
          {link("/challenges", "Challenges", "nav-challenges")}
          {link("/#why", "Why Us", "nav-why")}
          {link("/#process", "Process", "nav-process")}
          {link("/affiliate", "Affiliate", "nav-affiliate")}
          {link("/faq", "FAQ", "nav-faq")}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              {isAdmin && (
                <Link to="/admin" data-testid="nav-admin">
                  <Button variant="ghost" className="text-[#cfcfcf] hover:text-[#ff7a00] hover:bg-transparent">Admin</Button>
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard">
                <Button className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold glow-orange-soft">Dashboard</Button>
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="text-[#cfcfcf] hover:text-[#ff7a00] hover:bg-transparent">Log in</Button>
              </Link>
              <Link to="/signup" data-testid="nav-signup">
                <Button className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold glow-orange-soft">Get Funded</Button>
              </Link>
            </>
          )}
        </div>
        <button onClick={() => setOpen(!open)} className="lg:hidden text-[#f5f5f5]" aria-label="Menu" data-testid="nav-mobile-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#ff7a00]/10 px-6 py-6 flex flex-col gap-5 bg-[#0a0a0a]">
          {link("/", "Home", "nav-home-m")}
          {link("/challenges", "Challenges", "nav-challenges-m")}
          {link("/#why", "Why Us", "nav-why-m")}
          {link("/#process", "Process", "nav-process-m")}
          {link("/affiliate", "Affiliate", "nav-affiliate-m")}
          {link("/faq", "FAQ", "nav-faq-m")}
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard-m" onClick={() => setOpen(false)}>
              <Button className="w-full bg-[#ff7a00] text-[#050505]">Dashboard</Button>
            </Link>
          ) : (
            <div className="flex gap-3">
              <Link to="/login" className="flex-1" data-testid="nav-login-m" onClick={() => setOpen(false)}>
                <Button variant="outline" className="w-full border-[#ff7a00]/30 text-[#cfcfcf]">Log in</Button>
              </Link>
              <Link to="/signup" className="flex-1" data-testid="nav-signup-m" onClick={() => setOpen(false)}>
                <Button className="w-full bg-[#ff7a00] text-[#050505]">Sign up</Button>
              </Link>
            </div>
          )}
        </div>
      )}
      <WhatsAppButton />
    </header>
  );
};

export const Footer = () => (
  <footer className="border-t border-[#ff7a00]/15 bg-[#050505] py-12 mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
      <div className="col-span-2">
        <Logo />
        <p className="text-[#8a8a8a] text-sm mt-4 max-w-sm leading-relaxed">
          Elite Synthetic Index prop firm. Built for disciplined traders who demand institutional-grade tools and a fair path to funded capital.
        </p>
      </div>
      <div>
        <div className="text-[#f5f5f5] font-display font-semibold mb-3 text-sm">Platform</div>
        <ul className="space-y-2 text-sm text-[#8a8a8a]">
          <li><Link to="/challenges" className="hover:text-[#ff9a2f]" data-testid="footer-challenges">Challenges</Link></li>
          <li><Link to="/dashboard" className="hover:text-[#ff9a2f]" data-testid="footer-dashboard">Dashboard</Link></li>
          <li><Link to="/affiliate" className="hover:text-[#ff9a2f]" data-testid="footer-affiliate">Affiliate</Link></li>
          <li><Link to="/faq" className="hover:text-[#ff9a2f]" data-testid="footer-faq">FAQ</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-[#f5f5f5] font-display font-semibold mb-3 text-sm">Legal</div>
        <ul className="space-y-2 text-sm text-[#8a8a8a]">
          <li><Link to="/terms" className="hover:text-[#ff9a2f]" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
          <li><Link to="/policies" className="hover:text-[#ff9a2f]" data-testid="footer-policies">Privacy &amp; AML Policy</Link></li>
          <li><Link to="/disclaimer" className="hover:text-[#ff9a2f]" data-testid="footer-disclaimer">Risk Disclaimer</Link></li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-10 pt-6 border-t border-[#ff7a00]/10 text-xs text-[#8a8a8a] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div>&copy; 2026 Funded Index. All rights reserved.</div>
      <div>Trading involves substantial risk. Past performance is not indicative of future results.</div>
    </div>
  </footer>
);
